import React from 'react';
import { toastError, toastSuccess } from 'bench-internal-theme';
import { useClient } from '../../ClientContext';
import { useQuery, useMutation } from '@apollo/client';
import { showServerError } from 'bk-web-app';
import {
  GET_LEDGER_ACCOUNTS,
  UPDATE_LEDGER_MAPPING,
  UNMAP_LEDGER,
  GET_CONNECTIONS,
} from '../queries';
import { Select } from 'bench-internal-theme';
import { Account, ConnectionSource, LedgerAccount } from '../types';

const ledgerRoles = [
  'BANK_ACCOUNT',
  'CREDIT_CARD',
  'EQUITY_ACCOUNT',
  'STATEMENT_PROCESSING_ACCOUNT',
];

type LedgerSelectContainerProps = {
  account: Account;
  connectionSource: keyof typeof ConnectionSource;
};

const LedgerSelect = ({ account, connectionSource }: LedgerSelectContainerProps) => {
  const { id: clientId } = useClient();

  const { loading, error, data } = useQuery(GET_LEDGER_ACCOUNTS, {
    variables: { clientId },
  });

  const ledgers = data?.ledgerAccounts || [];

  const accountLedgerId = account.ledger?.id;

  const ledgersWithRole = ledgers.filter(
    (ledger: LedgerAccount) =>
      ledger.role &&
      ledgerRoles.includes(ledger.role) &&
      (!ledger.accountSettings?.fdsAccountId ||
        ledger.accountSettings?.fdsAccountId === account.id),
  );

  const options = ledgersWithRole.map((ledger: LedgerAccount) => ({
    value: ledger.id,
    label: ledger.name,
  }));

  const refetchQueries = [
    {
      query: GET_LEDGER_ACCOUNTS,
      variables: { clientId },
    },
    {
      query: GET_CONNECTIONS,
      variables: { clientId },
    },
  ];

  const [updateLedgerMapping, { loading: updateLedgerMappingLoading }] = useMutation(
    UPDATE_LEDGER_MAPPING,
    {
      awaitRefetchQueries: true,
      onCompleted: () => {
        toastSuccess('Account', 'Your account ledger mapping has been updated.');
      },
      onError: error => {
        toastError(
          'An error occurred mapping this account.',
          showServerError(error) || error.message,
        );
      },
      refetchQueries,
    },
  );

  const [unmapLedger, { loading: unmapLedgerLoading }] = useMutation(UNMAP_LEDGER, {
    awaitRefetchQueries: true,
    onCompleted: () => {
      toastSuccess('Ledger Account Un-Mapped', 'Your account has been un-mapped to the ledger.');
    },
    onError: error => {
      toastError('An error occurred unmapping this account.', showServerError(error));
    },
    refetchQueries,
  });

  const unmapAccount = () =>
    unmapLedger({
      variables: {
        accountId: account?.id,
        ledgerId: null,
        dryRun: false,
      },
    });

  const mapAccount = (ledgerId: string) =>
    updateLedgerMapping({
      variables: {
        clientId,
        connection: {
          source: connectionSource,
          id: account.id,
        },
        accountId: ledgerId,
      },
    });

  const onChange = option => {
    if (!option?.value && accountLedgerId) {
      return unmapAccount();
    }

    if (option?.value !== accountLedgerId) {
      return mapAccount(option.value);
    }
  };

  const getPlaceholderText = () => {
    if (error) {
      return 'Error fetching ledgers';
    }
    return 'Select...';
  };

  return (
    <Select
      aria-label="Ledger"
      value={options.find(option => option.value === accountLedgerId)}
      isClearable
      isSearchable={false}
      onChange={onChange}
      isDisabled={!!error || updateLedgerMappingLoading || unmapLedgerLoading}
      isLoading={loading}
      name="ledger"
      options={options}
      placeholder={getPlaceholderText()}
    />
  );
};

export default LedgerSelect;
